import {
  PolicyList,
  PolicyPage,
  PolicySection,
  PolicySubsection,
} from '../../components/PolicyPage';

const EFFECTIVE_DATE = 'May 20, 2026';
const LAST_UPDATED = 'October 6, 2026';

export function Terms() {
  return (
    <PolicyPage
      seoTitle="Terms of Service — AuctiSync"
      seoDescription="Terms governing your use of the AuctiSync auction platform, operated by Welcome Home Apps LLC."
      canonicalPath="/terms"
      title="Terms of Service"
      effectiveDate={EFFECTIVE_DATE}
      lastUpdated={LAST_UPDATED}
    >
      <p>
        These Terms of Service (the &ldquo;Terms&rdquo;) govern your access to and use of the
        AuctiSync auction platform and related services operated by Welcome Home Apps LLC, a
        Missouri limited liability company (&ldquo;Welcome Home Apps,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; or &ldquo;our&rdquo;), including the websites at auctisync.com,
        app.auctisync.com and help.auctisync.com, auction houses&apos; own domains connected to
        AuctiSync, and our iOS and Android applications (collectively, the &ldquo;Services&rdquo;).
      </p>
      <p>
        By creating an account or otherwise using the Services, you agree to these Terms and our{' '}
        <a href="/privacy">Privacy Policy</a>. If you do not agree, do not use the Services.
      </p>

      <PolicySection title="1. Eligibility and Account Registration">
        <p>
          You must be at least 18 years old and capable of forming a binding contract under the laws
          of your jurisdiction. By using the Services, you represent that you meet these
          requirements.
        </p>
        <p>
          To access most features, you must register for an account. You agree to provide accurate
          information, keep it up to date, and maintain the confidentiality of your credentials. You
          are responsible for activity that occurs under your account, except to the extent caused
          by our breach of these Terms.
        </p>
      </PolicySection>

      <PolicySection title="2. The Services">
        <p>
          AuctiSync is a software platform that enables (a) auction operators
          (&ldquo;Tenants&rdquo;) to publish online auctions, accept bids, settle payments, and
          fulfill won lots, and (b) buyers (&ldquo;Bidders&rdquo;) to register, place bids, win
          lots, and pay invoices.
        </p>
        <p>
          We provide the Services as a software-as-a-service intermediary. We are not the seller,
          consignor, auctioneer, or fiduciary of any item listed on the Services. Each Tenant is
          solely responsible for the items it lists, the descriptions and photos it provides, the
          terms of its auctions, the legality of the sale, the conduct of the auction, and the
          delivery or pickup of won items.
        </p>
      </PolicySection>

      <PolicySection title="3. Auction House Terms">
        <p>
          If you use AuctiSync to run auctions (a &ldquo;Tenant&rdquo;), our{' '}
          <a href="/subscriber-agreement">Subscriber Agreement</a> also applies to you. It covers
          your subscription and fees, payments from your bidders, your responsibilities as an
          auctioneer, and your data. Where the Subscriber Agreement and these Terms conflict for a
          Tenant, the Subscriber Agreement controls.
        </p>
      </PolicySection>

      <PolicySection title="4. Bidder Terms">
        <PolicySubsection title="4.1 Bids Are Binding">
          <p>
            <strong>
              Every bid you place is a binding offer to purchase the lot at that price.
            </strong>{' '}
            If you place the winning bid, you are contractually obligated to pay the winning amount
            plus any applicable buyer&apos;s premium, taxes, fees, and shipping or pickup charges
            within the timeframe set by the Tenant.
          </p>
          <p>
            We reserve the right to retract obvious typographical errors and to retract bids placed
            by users we believe are acting fraudulently, but you should treat every bid as final at
            the moment you submit it.
          </p>
        </PolicySubsection>

        <PolicySubsection title="4.2 Anti-Snipe and Closing">
          <p>
            Auctions may use anti-snipe protections that extend a lot&apos;s closing time when a bid
            is placed in the final seconds. Closing times shown in our interfaces are
            server-authoritative; the bidder&apos;s local clock is not the source of truth.
          </p>
        </PolicySubsection>

        <PolicySubsection title="4.3 Pickup, Shipping, and Buyer's Premium">
          <p>
            Each Tenant sets its own pickup or shipping terms, buyer&apos;s premium, deposit
            requirements, and pickup-window deadlines. By placing a bid, you confirm that you have
            read and agree to the Tenant&apos;s auction-specific terms.
          </p>
        </PolicySubsection>

        <PolicySubsection title="4.4 Pre-Authorization Holds">
          <p>
            Some Tenants require a pre-authorization hold on a credit card as a condition of
            bidding. Authorizations are managed by Stripe; we never store full card numbers. Holds
            are released according to Stripe&apos;s standard timelines if you do not win.
          </p>
        </PolicySubsection>

        <PolicySubsection title="4.5 Disputes With Tenants">
          <p>
            If you have a dispute with a Tenant about an item, an invoice, or auction conduct,
            contact the Tenant directly first. If you cannot resolve the issue, contact{' '}
            <a href="mailto:support@auctisync.com">support@auctisync.com</a>. We are not a party to
            the bidder-tenant contract and we do not guarantee tenant performance, but we will
            assist where reasonably able.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="5. Prohibited Content and Conduct">
        <p>You agree not to use the Services to:</p>
        <PolicyList
          items={[
            <span>
              List or attempt to list{' '}
              <strong>firearms, ammunition, firearm components, or explosives</strong>. Firearms and
              ammunition are prohibited on AuctiSync regardless of license status.
            </span>,
            <span>
              List or attempt to list{' '}
              <strong>
                illegal drugs, controlled substances, drug paraphernalia, or prescription
                medications
              </strong>
              .
            </span>,
            <span>
              List or attempt to list <strong>tobacco, vaping products, or alcohol</strong> without
              all required licenses and our prior written approval.
            </span>,
            <span>
              List or attempt to list{' '}
              <strong>
                human remains, body parts, ivory, endangered species, or items in violation of the
                Endangered Species Act, CITES, or the African Elephant Conservation Act
              </strong>
              .
            </span>,
            <span>
              List or attempt to list{' '}
              <strong>
                stolen goods, counterfeit goods, or items the Tenant does not have legal authority
                to sell
              </strong>
              .
            </span>,
            <span>
              List or attempt to list <strong>hazardous materials</strong> that cannot be lawfully
              shipped under U.S. Department of Transportation regulations.
            </span>,
            <span>
              Upload, transmit, or store <strong>child sexual abuse material (CSAM)</strong> or any
              content that sexually exploits minors. We report apparent CSAM to the National Center
              for Missing &amp; Exploited Children (NCMEC) as required by 18 U.S.C. § 2258A, remove
              it, and terminate the accounts involved.
            </span>,
            <span>
              Engage in <strong>shill bidding</strong>, including bidding on your own auctions or
              having affiliates or employees bid on your auctions.
            </span>,
            <span>
              Engage in <strong>bid retraction abuse</strong>, including placing bids you have no
              intention of honoring or repeatedly withdrawing bids.
            </span>,
            <span>
              Use the Services to <strong>collude with other users</strong> to suppress prices or
              manipulate auctions.
            </span>,
            <span>
              Reverse-engineer, scrape at scale, decompile, or attempt to derive the source code of
              the Services, except to the extent applicable law expressly permits.
            </span>,
            <span>
              Use any robot, spider, or automated means to access the Services other than through
              public APIs that we authorize.
            </span>,
            <span>
              Interfere with or disrupt the Services, including by transmitting malware or
              conducting denial-of-service attacks.
            </span>,
            <span>Use the Services to harass, defame, or threaten any other user.</span>,
          ]}
        />
        <p>
          We may, in our sole discretion, remove content, suspend listings, suspend accounts, or
          terminate accounts that violate these Terms or that we reasonably believe pose a risk to
          users, third parties, or the platform.
        </p>
      </PolicySection>

      <PolicySection title="6. Intellectual Property">
        <PolicySubsection title="6.1 Our IP">
          <p>
            The Services, including all software, text, graphics, logos, sounds, designs, and
            user-interface elements (other than User Content as defined below), are owned by Welcome
            Home Apps LLC and protected by U.S. and international intellectual-property laws.
            Welcome Home Apps grants you a limited, revocable, non-exclusive, non- transferable
            license to use the Services in accordance with these Terms.
          </p>
        </PolicySubsection>

        <PolicySubsection title="6.2 User Content">
          <p>
            You retain all rights in the content you upload, post, or transmit through the Services
            (&ldquo;User Content&rdquo;). By submitting User Content, you grant Welcome Home Apps a
            worldwide, non-exclusive, royalty-free license to host, store, reproduce, display,
            distribute, and process the User Content solely as needed to provide the Services,
            including caching for delivery, generating thumbnails, indexing for search, and creating
            derivative works (such as image compositing or AI-assisted descriptions) necessary to
            operate features the User has enabled.
          </p>
          <p>
            You represent and warrant that you have all rights necessary to grant this license, and
            that your User Content does not infringe or violate any third-party right.
          </p>
        </PolicySubsection>

        <PolicySubsection title="6.3 Feedback">
          <p>
            If you send us feedback, ideas, or suggestions about the Services, you grant us a
            perpetual, irrevocable, royalty-free, transferable license to use that feedback for any
            purpose without obligation to you.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="7. Copyright (DMCA)">
        <p>
          We respect the intellectual-property rights of others and respond to clear notices of
          alleged copyright infringement that comply with the Digital Millennium Copyright Act of
          1998 (the &ldquo;DMCA&rdquo;). Send DMCA notices to our designated agent:
        </p>
        <p>
          <strong>DMCA Agent — Welcome Home Apps LLC</strong>
          <br />
          <a href="mailto:dmca@auctisync.com">dmca@auctisync.com</a>
        </p>
        <p>
          Our designated agent&apos;s full contact details are available on request by writing to
          the email above.
        </p>
        <p>
          A valid DMCA notice must include: (i) a physical or electronic signature of a person
          authorized to act on behalf of the copyright owner; (ii) identification of the copyrighted
          work claimed to have been infringed; (iii) identification of the allegedly infringing
          material and its URL on the Services; (iv) your contact information; (v) a statement that
          you have a good-faith belief that the use is not authorized; and (vi) a statement, under
          penalty of perjury, that the information in the notice is accurate and that you are
          authorized to act. We may terminate accounts of repeat infringers.
        </p>
      </PolicySection>

      <PolicySection title="8. Image and Content Scanning">
        <p>
          We may use automated systems to review uploaded images and content for safety, abuse
          prevention and legal compliance &mdash; for example, checking images against known
          child-safety hash databases, detecting prohibited items, finding duplicate listings, or
          reading text in images to make it searchable. Our staff may also review content in
          response to flags or user reports.
        </p>
      </PolicySection>

      <PolicySection title="9. Disclaimers">
        <p>
          THE SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE,&rdquo; WITHOUT
          WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION IMPLIED
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY
          WARRANTIES ARISING FROM COURSE OF DEALING OR USAGE OF TRADE. WE DO NOT WARRANT THAT THE
          SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE FROM VIRUSES OR OTHER HARMFUL
          COMPONENTS.
        </p>
        <p>
          WE MAKE NO WARRANTY REGARDING THE QUALITY, ACCURACY, COMPLETENESS, OR LEGALITY OF ANY ITEM
          LISTED BY A TENANT, AND WE ARE NOT RESPONSIBLE FOR ANY TRANSACTION BETWEEN A BIDDER AND A
          TENANT. INSPECT ITEMS BEFORE BIDDING WHENEVER POSSIBLE.
        </p>
      </PolicySection>

      <PolicySection title="10. Limitation of Liability">
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, WELCOME HOME APPS LLC AND ITS OFFICERS, DIRECTORS,
          EMPLOYEES, AGENTS, AND SERVICE PROVIDERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
          SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA,
          GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF THE
          SERVICES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          OUR TOTAL CUMULATIVE LIABILITY ARISING OUT OF OR RELATING TO THE SERVICES WILL NOT EXCEED
          THE GREATER OF (A) ONE HUNDRED DOLLARS ($100) OR (B) THE TOTAL AMOUNT YOU PAID US IN THE
          TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
        </p>
      </PolicySection>

      <PolicySection title="11. Indemnification">
        <p>
          You agree to indemnify, defend, and hold harmless Welcome Home Apps LLC and its officers,
          directors, employees, agents, and service providers from and against any claims,
          liabilities, damages, losses, costs, and expenses (including reasonable attorneys&apos;
          fees) arising out of or related to: (a) your use of the Services; (b) your User Content;
          (c) your violation of these Terms; or (d) your violation of any law or third-party right.
        </p>
      </PolicySection>

      <PolicySection title="12. Termination">
        <p>
          You may close your account at any time by emailing{' '}
          <a href="mailto:support@auctisync.com">support@auctisync.com</a>. We may suspend or
          terminate your access to the Services at any time, with or without cause and with or
          without notice, including for violation of these Terms. Sections of these Terms that by
          their nature should survive termination will survive, including without limitation
          Sections 6, 9, 10, 11, 13, and 14.
        </p>
      </PolicySection>

      <PolicySection title="13. Dispute Resolution and Governing Law">
        <p>
          These Terms are governed by the laws of the State of Missouri, without regard to its
          conflict-of-laws principles. The Parties agree that any dispute, claim, or controversy
          arising out of or relating to these Terms or the Services will be brought exclusively in
          the state or federal courts located in St. Louis County, Missouri, and you consent to
          personal jurisdiction in those courts.
        </p>
        <p>
          You and Welcome Home Apps each waive any right to a jury trial in connection with any such
          dispute.
        </p>
      </PolicySection>

      <PolicySection title="14. Changes to These Terms">
        <p>
          We may update these Terms from time to time. The &ldquo;Last updated&rdquo; date at the
          top reflects the most recent revision. If we make material changes, we will notify account
          holders by email at least 30 days before the changes take effect. Your continued use of
          the Services after the changes take effect constitutes acceptance of the updated Terms.
        </p>
      </PolicySection>

      <PolicySection title="15. Miscellaneous">
        <PolicyList
          items={[
            <span>
              <strong>Entire Agreement.</strong> These Terms, together with the{' '}
              <a href="/privacy">Privacy Policy</a>, the{' '}
              <a href="/subscriber-agreement">Subscriber Agreement</a> (for Tenants), and any
              auction-specific terms posted by a Tenant, constitute the entire agreement between you
              and Welcome Home Apps regarding the Services.
            </span>,
            <span>
              <strong>Severability.</strong> If any provision of these Terms is held unenforceable,
              that provision will be modified to the minimum extent necessary to make it
              enforceable, and the remaining provisions will continue in full force and effect.
            </span>,
            <span>
              <strong>No Waiver.</strong> Our failure to enforce any right or provision of these
              Terms will not be considered a waiver of that right or provision.
            </span>,
            <span>
              <strong>Assignment.</strong> You may not assign these Terms without our prior written
              consent. We may assign these Terms in connection with a merger, acquisition,
              financing, or sale of all or a portion of our assets.
            </span>,
            <span>
              <strong>Notices.</strong> We may give notice to you by email to the address on file,
              by posting on the Services, or by any other reasonable means. You must give notice to
              us at <a href="mailto:support@auctisync.com">support@auctisync.com</a>.
            </span>,
            <span>
              <strong>Force Majeure.</strong> Neither party will be liable for failure or delay in
              performance caused by circumstances beyond its reasonable control.
            </span>,
          ]}
        />
      </PolicySection>

      <PolicySection title="16. Contact">
        <p>Questions about these Terms can be sent to:</p>
        <p>
          <strong>Welcome Home Apps LLC</strong>
          <br />
          Attn: Legal
          <br />
          <a href="mailto:support@auctisync.com">support@auctisync.com</a>
        </p>
      </PolicySection>
    </PolicyPage>
  );
}
